import React from 'react';
import { Modal, Button } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';

/**
 * Reusable confirmation modal for delete operations
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} toggle - Function to toggle modal visibility
 * @param {function} onConfirm - Function to call when delete is confirmed
 * @param {string} title - Modal title (default: "Confirm Delete")
 * @param {string} message - Confirmation message
 * @param {string} confirmText - Confirm button text (default: "Delete")
 * @param {string} cancelText - Cancel button text (default: "Cancel")
 * @param {string} confirmColor - Confirm button color (default: "danger")
 * @param {React.ReactNode} children - Optional custom content
 * @param {boolean} loading - Whether the delete operation is in progress
 */
const ConfirmDeleteModal = ({
  isOpen,
  toggle,
  onConfirm,
  title = 'Confirm Delete',
  message = 'Are you sure you want to delete this item? This action cannot be undone.',
  confirmText = 'Delete',
  cancelText = 'Cancel',
  confirmColor = 'danger',
  children,
  loading = false,
}) => {
  const { t } = useTranslation();
  
  const displayTitle = title === 'Confirm Delete' ? t('shared.confirm_delete', 'Confirm Delete') : title;
  const displayMessage = message === 'Are you sure you want to delete this item? This action cannot be undone.' ? t('business.components.confirm_delete_modal.message', 'Are you sure you want to delete this item? This action cannot be undone.') : message;
  const displayConfirmText = confirmText === 'Delete' ? t('shared.delete', 'Delete') : confirmText;
  const displayCancelText = cancelText === 'Cancel' ? t('shared.cancel', 'Cancel') : cancelText;

  const handleConfirm = () => {
    onConfirm();
    if (!loading) {
      toggle();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <div className="modal-header">
        <h5 className="modal-title">{displayTitle}</h5>
        <button
          type="button"
          className="btn-close"
          onClick={toggle}
          aria-label="Close"
          disabled={loading}
        />
      </div>
      <div className="modal-body">
        {children || (
          <div className="text-center">
            <FeatherIcon icon="alert-triangle" size="48" className="text-warning mb-3" />
            <p>{displayMessage}</p>
          </div>
        )}
      </div>
      <div className="modal-footer">
        <Button type="button" color="secondary" onClick={toggle} disabled={loading}>
          {displayCancelText}
        </Button>
        <Button type="button" color={confirmColor} onClick={handleConfirm} disabled={loading}>
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              {t('shared.deleting', 'Deleting...')}
            </>
          ) : (
            displayConfirmText
          )}
        </Button>
      </div>
    </Modal>
  );
};

export default ConfirmDeleteModal;
